/**
 * <analytics-drill-provenance> — the account of one figure: what it was asked
 * for, what it was read out of, and why it disagrees with the number beside it.
 *
 * THE THREE SECTIONS ARE THREE DIFFERENT DOUBTS. "Is this the range I set" is
 * the derivation; "where did this come from" is the collections; "why does the
 * tile next to it say something else" is the neighbour. An operator arrives
 * with one of the three, so they are separated rather than run together as one
 * paragraph they would have to read all of.
 *
 * THE SENSITIVITY CURVE IS A ROW OF RUNGS, NOT A CHART. It is five numbers
 * whose only question is "does this figure survive a different definition of a
 * visit" — a comparison a reader makes by reading two of them, which a row of
 * labelled values answers directly. A plotted distribution would put a shape
 * between the reader and five numbers they were going to read anyway, and
 * would need a primitive the library does not carry.
 *
 * FRESHNESS IS A COUNT PLUS ITS EXCEPTIONS. A mark per day is thirty identical
 * marks in which the one day that differs disappears, so the count carries the
 * rule and the named days carry the exceptions. Each named day arrives as the
 * date it is and is printed here, in this reader's own weekday and month.
 *
 * THE NEIGHBOUR SECTION IS THE ONE THAT EARNS THE PANEL. A surface printing
 * both Visits and Browsers is printing two counts of what a reader thinks is
 * one thing; without the reason, one of them looks like a bug. It is absent
 * where no second figure of the same thing is printed — a section invented to
 * fill the shape would be prose about nothing.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { formatFigure } from "../analytics-figure.view.ts";
import type { ProvenanceAnswer } from "./drill.state.ts";
import { phraseText } from "./drill.state.ts";

@schmancyElement("analytics-drill-provenance")
export class AnalyticsDrillProvenance extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	@property({ attribute: false }) answer!: ProvenanceAnswer;

	override render() {
		return html`
			<div class="flex flex-col gap-5">
				<div class="flex items-baseline justify-between gap-3">
					<schmancy-typography
						type="label"
						token="sm"
						class="text-surface-onVariant"
					>
						${this.t("admin.analytics.drill.provDerivation")}
					</schmancy-typography>
					<schmancy-typography type="title" token="md" class="tabular-nums">
						${formatFigure(this.answer.whole, this.answer.unit, this.locale)}
					</schmancy-typography>
				</div>

				<dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
					${repeat(
						this.answer.derivation,
						(row) => phraseText(row.label, this),
						(row) => html`
							<dt>
								<schmancy-typography
									type="label"
									token="sm"
									class="text-surface-onVariant"
								>
									${phraseText(row.label, this)}
								</schmancy-typography>
							</dt>
							<dd class="min-w-0">
								<schmancy-typography type="body" token="sm">
									${phraseText(row.value, this)}
								</schmancy-typography>
							</dd>
						`,
					)}
				</dl>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="md">
						${this.t("admin.analytics.drill.provSourcesTitle")}
					</schmancy-typography>
					${repeat(
						this.answer.sources,
						(source) => source.path,
						(source) => html`
							<div class="flex flex-col gap-0.5">
								<div class="flex items-baseline justify-between gap-3">
									<schmancy-typography
										type="label"
										token="sm"
										class="font-mono text-primary-default"
									>
										${source.path}
									</schmancy-typography>
									<schmancy-typography
										type="label"
										token="sm"
										class="text-surface-onVariant tabular-nums shrink-0"
									>
										${this.t("admin.analytics.drill.provDocuments", {
											count: formatFigure(
												source.documents,
												"count",
												this.locale,
											),
										})}
									</schmancy-typography>
								</div>
								<schmancy-typography
									type="body"
									token="sm"
									class="text-surface-onVariant"
								>
									${phraseText(source.read, this)}
								</schmancy-typography>
							</div>
						`,
					)}
				</div>

				<div class="flex flex-col gap-1">
					<schmancy-typography type="label" token="md">
						${this.t("admin.analytics.drill.provFreshnessTitle")}
					</schmancy-typography>
					<schmancy-typography type="body" token="sm">
						${
							this.answer.freshness.stored === 0
								? this.t("admin.analytics.drill.provFreshnessAllFresh", {
										total: this.answer.freshness.total,
									})
								: this.t("admin.analytics.drill.provFreshnessCount", {
										stored: this.answer.freshness.stored,
										total: this.answer.freshness.total,
									})
						}
					</schmancy-typography>
					${when(
						this.answer.freshness.stored > 0 &&
							this.answer.freshness.recomputed.length > 0,
						() => html`
							<schmancy-typography
								type="body"
								token="sm"
								class="text-surface-onVariant"
							>
								${repeat(
									this.answer.freshness.recomputed,
									(day) => phraseText(day, this),
									(day, index) =>
										html`${index > 0 ? " · " : ""}${this.t(
											"admin.analytics.drill.provFreshnessDay",
											{ day: phraseText(day, this) },
										)}`,
								)}
							</schmancy-typography>
						`,
					)}
				</div>

				${when(
					this.answer.sensitivity.length > 0,
					() => html`
						<div class="flex flex-col gap-2">
							<schmancy-typography type="label" token="md">
								${this.t("admin.analytics.drill.provSensitivityTitle")}
							</schmancy-typography>
							<schmancy-typography
								type="body"
								token="sm"
								class="text-surface-onVariant"
							>
								${this.t("admin.analytics.drill.provSensitivityLead")}
							</schmancy-typography>
							<div class="flex flex-wrap gap-2">
								${repeat(
									this.answer.sensitivity,
									(rung) => rung.minutes,
									(rung) => html`
										<schmancy-surface
											type=${rung.current ? "containerHighest" : "containerLow"}
											class="flex flex-col gap-0.5 rounded-lg px-3 py-2"
										>
											<schmancy-typography
												type="label"
												token="xs"
												class="text-surface-onVariant"
											>
												${rung.minutes} min${
													rung.current
														? ` · ${this.t("admin.analytics.drill.provSensitivityCurrent")}`
														: ""
												}
											</schmancy-typography>
											<schmancy-typography
												type="title"
												token="sm"
												class="tabular-nums"
											>
												${formatFigure(rung.visits, "count", this.locale)}
											</schmancy-typography>
										</schmancy-surface>
									`,
								)}
							</div>
						</div>
					`,
				)}

				${when(
					this.answer.neighbour !== null,
					() => html`
						<div class="flex flex-col gap-2">
							<schmancy-typography type="label" token="md">
								${this.t("admin.analytics.drill.provNeighbourTitle")}
							</schmancy-typography>
							<schmancy-typography type="body" token="md">
								${phraseText(this.answer.neighbour?.claim ?? { text: "" }, this)}
							</schmancy-typography>
							<schmancy-typography
								type="body"
								token="sm"
								class="text-surface-onVariant"
							>
								${phraseText(this.answer.neighbour?.because ?? { text: "" }, this)}
							</schmancy-typography>
						</div>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-drill-provenance": AnalyticsDrillProvenance;
	}
}
